import React from "react";
import { X, DownloadSimple, ShareNetwork, PencilSimple, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AssetPreviewModal — modale universale di "vedo l'asset finito" per
 * Materiali (PDF, immagini, testo). Iter 272 round 2.
 *
 * Visual layout:
 *   - Header: titolo + close
 *   - Body: <iframe> per PDF · <img> per PNG/JPG · <pre> per text
 *   - Footer: Scarica · Condividi · Modifica · Apri in nuova scheda
 *
 * Props:
 *   open, onClose
 *   kind: "pdf" | "image" | "text"
 *   src: URL (pdf/image) or string content (text)
 *   filename
 *   title
 *   onShare?: () => void   (override del WebShare default)
 *   onEdit?: () => void    (es. apre tab Annuncio/Testo)
 *   editLabel?: string     (default "Modifica")
 */
export default function AssetPreviewModal({
  open, onClose, kind, src, filename, title, subtitle,
  onShare, onEdit, editLabel = "Modifica",
}) {
  if (!open) return null;

  const onDownload = async () => {
    try {
      if (kind === "text") {
        const blob = new Blob([src], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename || "asset.txt";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } else {
        const res = await fetch(src);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename || "asset";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success("File scaricato");
    } catch (e) {
      toast.error("Download non riuscito");
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(src);
      toast.success("Testo copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const defaultShare = async () => {
    try {
      if (kind === "text" && navigator.share) {
        await navigator.share({ title, text: src });
      } else if (navigator.share) {
        await navigator.share({ title, url: src });
      } else {
        await navigator.clipboard.writeText(kind === "text" ? src : src);
        toast.success("Copiato negli appunti");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non riuscita");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/55 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="asset-preview-modal"
    >
      <div
        className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#F0EFE9]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
              Anteprima
            </div>
            <h2 className="font-editorial text-xl text-[#1C1C1A] mt-0.5 truncate">{title}</h2>
            {subtitle && <div className="text-[11.5px] text-[#9A968B] mt-0.5">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full hover:bg-[#F1F4ED] inline-flex items-center justify-center text-[#1C1C1A]"
            aria-label="Chiudi"
            data-testid="asset-preview-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-[#FAFAF7]">
          {kind === "pdf" && (
            <iframe
              src={src + "#toolbar=0&navpanes=0"}
              title={title}
              className="w-full h-[60vh] sm:h-[65vh]"
              data-testid="asset-preview-pdf"
            />
          )}
          {kind === "image" && (
            <div className="flex items-center justify-center p-4">
              <img src={src} alt={title} className="max-w-full max-h-[60vh] object-contain" data-testid="asset-preview-image" />
            </div>
          )}
          {kind === "text" && (
            <pre
              className="px-5 py-5 text-[13.5px] leading-[1.7] text-[#1C1C1A] whitespace-pre-wrap font-sans break-words"
              data-testid="asset-preview-text"
            >
              {src}
            </pre>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-[#F0EFE9] bg-white">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[12.5px] font-semibold transition-colors"
              data-testid="asset-preview-download"
            >
              <DownloadSimple size={13} weight="bold" /> Scarica
            </button>
            {kind === "text" && (
              <button
                onClick={onCopy}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] text-[12px] font-semibold transition-colors"
                data-testid="asset-preview-copy"
              >
                <Copy size={12} weight="bold" /> Copia
              </button>
            )}
            <button
              onClick={onShare || defaultShare}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] text-[12px] font-semibold transition-colors"
              data-testid="asset-preview-share"
            >
              <ShareNetwork size={12} weight="bold" /> Condividi
            </button>
            {onEdit && (
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] text-[12px] font-semibold transition-colors"
                data-testid="asset-preview-edit"
              >
                <PencilSimple size={12} weight="bold" /> {editLabel}
              </button>
            )}
            {kind === "pdf" && (
              <a
                href={src}
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium px-2 py-1 transition-colors"
                data-testid="asset-preview-newtab"
              >
                <ArrowSquareOut size={11} weight="bold" /> Apri in nuova scheda
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
